"use client";

import { useEffect, useRef, useId, useMemo, useState } from "react";
import { Badge } from "@modernatc/ui/badge";
import { Input } from "@modernatc/ui/input";
import { Label } from "@modernatc/ui/label";
import {
  classifyEntry,
  entryInstructions,
  entrySectorWidth,
  explainEntry,
  formatCourse,
  holdInstructions,
  normalizeCourse,
  relativeAngle,
  type Entry,
  type Turn,
} from "@/lib/hold-entry";

const CX = 190;
const CY = 190;
const RING = 160;
const TURN_RADIUS = 22;
const LEG_LENGTH = 70;
const HOLD_WIDTH = 44;
const FLY_SECONDS = 5;
const PAUSE_SECONDS = 0;
const CYCLE_SECONDS = FLY_SECONDS + PAUSE_SECONDS; // 10

// Dwell at the start of the path (the aircraft) for the pause, then fly.
// keyPoints are fractions of path length; keyTimes map them onto the cycle.
const DOT_KEY_TIMES = `0;${PAUSE_SECONDS / CYCLE_SECONDS};1`; // "0;0.2;1"
const DOT_KEY_POINTS = "0;0;1";

const ENTRY_COLORS: Record<Entry, string> = {
  direct: "96,165,250",
  teardrop: "239,159,39",
  parallel: "226,75,74",
};

interface Point {
  x: number;
  y: number;
}

interface ArcResult {
  d: string;
  end: Point;
}

interface HoldGeometry {
  loop: string;
  continuation: string;
}

interface EntryPathResult {
  d: string;
  entry: Entry;
}

export function HoldEntryVisualizer() {
  const [inbound, setInbound] = useState(360);
  const [heading, setHeading] = useState(200);
  const [turn, setTurn] = useState<Turn>("right");

  const entry = classifyEntry(heading, inbound, turn);
  const relative = relativeAngle(heading, inbound);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)] p-6">
        <h2 className="sr-only">
          Interactive holding pattern diagram. Adjust inbound course, aircraft
          heading, and turn direction to see the recommended entry, dashed entry
          track, and solid hold racetrack.
        </h2>

        <HoldDiagram inbound={inbound} heading={heading} turn={turn} />
        <div className="space-y-5 mt-2">
        <RangeControl
          id="inbound"
          label="Inbound holding course"
          value={inbound}
          onChange={setInbound}
          help="The course flown TO the holding fix on the inbound leg."
        />

        <RangeControl
          id="heading"
          label="Aircraft heading"
          value={heading}
          onChange={setHeading}
          help="Direction the aircraft is pointed while approaching the fix."
        />

        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-[var(--color-ink-dim)]">
            Turn direction
          </Label>

          <div className="mt-2 grid grid-cols-2 gap-2">
            <ToggleButton
              active={turn === "right"}
              onClick={() => setTurn("right")}
              label="Right (standard)"
            />
            <ToggleButton
              active={turn === "left"}
              onClick={() => setTurn("left")}
              label="Left"
            />
          </div>

          <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
            Standard holds use right turns. Left turns must be assigned or
            published.
          </p>
        </div>
      </div>
      </div>
      <EntryPanel
          entry={entry}
          turn={turn}
          relative={relative}
          inbound={inbound}
          heading={heading}
          />
    </div>
  );
}

function RangeControl({
  id,
  label,
  value,
  onChange,
  help,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  help: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <Label
          htmlFor={id}
          className="text-xs uppercase tracking-[0.18em] text-[var(--color-ink-dim)]"
        >
          {label}
        </Label>

        <output className="font-mono text-sm text-[var(--color-ink)]">
          {displayCourse(value)}°
        </output>
      </div>

      <div className="mt-2 grid grid-cols-[1fr_82px] items-center gap-3">
        <input
          id={id}
          type="range"
          min={0}
          max={360}
          step={1}
          value={value}
          onChange={(e) => onChange(clampCourseInput(Number(e.target.value)))}
          className="w-full accent-[var(--color-scope)]"
        />

        <Input
          type="number"
          min={0}
          max={360}
          value={value}
          onChange={(e) => onChange(clampCourseInput(Number(e.target.value)))}
          className="font-mono text-sm"
        />
      </div>

      <p className="mt-1 text-xs text-[var(--color-ink-muted)]">{help}</p>
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-[var(--radius-sm)] border px-3 py-2 text-sm transition ${
        active
          ? "border-[var(--color-scope)] bg-[var(--color-scope)]/15 text-[var(--color-scope)]"
          : "border-[var(--color-panel-edge)] bg-[var(--color-panel)] text-[var(--color-ink-dim)] hover:bg-[var(--color-panel-raised)]/40"
      }`}
    >
      {label}
    </button>
  );
}

function EntryPanel({
  entry,
  turn,
  relative,
  inbound,
  heading,
}: {
  entry: Entry;
  turn: Turn;
  relative: number;
  inbound: number;
  heading: number;
}) {
  const color = `rgb(${ENTRY_COLORS[entry]})`;
  const name = entry.charAt(0).toUpperCase() + entry.slice(1);
  const relRounded = Math.round(relative);
  const relText = relRounded > 0 ? `+${relRounded}` : `${relRounded}`;

  const entrySteps = entryInstructions(entry, inbound, heading, turn);
  const holdSteps = holdInstructions(inbound, turn);
  
  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)] p-4">
      <div className="flex items-center gap-3">
        <span
          className="inline-block size-3 rounded-[3px]"
          style={{ backgroundColor: color }}
        />
        <h3 className="text-2xl font-semibold text-[var(--color-ink)]">
          {name}
        </h3>
        <Badge variant="muted">{entrySectorWidth(entry)}° sector</Badge>
      </div>

      <div className="mt-1 text-xs uppercase tracking-[0.16em] text-[var(--color-ink-muted)]">
        Relative heading {relText}°
      </div>

      <p className="mt-4 text-sm leading-6 text-[var(--color-ink-dim)]">
        {explainEntry(entry, turn)}
      </p>

      <StepList title="Entry Instructions" steps={entrySteps} />
      <StepList title="Hold Instructions" steps={holdSteps} />

      <div className="mt-4 border-t border-[var(--color-panel-edge)] pt-3">
        <LegendLine color={color} dashed label="Entry track" />
        <LegendLine color="var(--color-ink-dim)" label="Hold pattern" />
      </div>
    </div>
  );
}

function StepList({ title, steps }: { title: string; steps: string[] }) {
  return (
    <div className="mt-4">
      <div className="text-xs uppercase tracking-[0.16em] text-[var(--color-ink-muted)]">
        {title}
      </div>

      <ol className="mt-2 space-y-1.5">
        {steps.map((step, index) => (
          <li
            key={index}
            className="flex gap-2.5 text-sm leading-6 text-[var(--color-ink-dim)]"
          >
            <span className="pt-px font-mono text-xs tabular-nums text-[var(--color-ink-muted)]">
              {index + 1}.
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function LegendLine({
  color,
  dashed = false,
  label,
}: {
  color: string;
  dashed?: boolean;
  label: string;
}) {
  return (
    <div className="mt-2 flex items-center gap-2 text-xs text-[var(--color-ink-muted)]">
      <svg width="28" height="8" aria-hidden="true">
        <line
          x1="0"
          y1="4"
          x2="28"
          y2="4"
          stroke={color}
          strokeWidth="2.5"
          strokeDasharray={dashed ? "5 4" : undefined}
        />
      </svg>
      <span>{label}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Diagram
// ─────────────────────────────────────────────────────────────────────────────

function HoldDiagram({
  inbound,
  heading,
  turn,
}: {
  inbound: number;
  heading: number;
  turn: Turn;
}) {
  const idBase = useId().replace(/:/g, "");
  const markerId = `${idBase}-he-dir`;
  const reducedMotion = usePrefersReducedMotion();
  const animateRef = useRef<SVGElement>(null);
  const entry = classifyEntry(heading, inbound, turn);

  const hold = useMemo(() => holdGeometry(inbound, turn), [inbound, turn]);

  const entryPath = useMemo(
    () => buildEntryPath(inbound, heading, turn),
    [inbound, heading, turn],
  );
  
  const aircraft = toXY((heading + 180) % 360, RING - 35);
  const geometryKey = `${inbound}-${heading}-${turn}-${entry}`;
  const animationKey = useDebouncedValue(geometryKey, 120);
  const motionPath = `${entryPath.d} ${hold.continuation}`;
  useEffect(() => {
    if (reducedMotion) return;
    (animateRef.current as SVGAnimateMotionElement | null)?.beginElement();
  }, [animationKey, reducedMotion]);
  const sectors = useMemo(
    () => buildSectorWedges(inbound, turn, entry),
    [inbound, turn, entry],
  );

  return (
    <div className="flex flex-col items-center">
      <svg
        width="100%"
        viewBox="0 0 380 380"
        role="img"
        aria-label={`Holding entry and hold path diagram. Inbound ${inbound} degrees. Aircraft heading ${heading} degrees. ${turn} turns. Entry ${entry}.`}
        className="max-w-[430px]"
      >
        <title>Holding entry and hold path diagram</title>

        <defs>
          <marker
            id={markerId}
            viewBox="0 0 10 10"
            refX="5"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto"
          >
            <path
              d="M1.5 1.5 L8.5 5 L1.5 8.5"
              fill="none"
              stroke="context-stroke"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </marker>
        </defs>

        {sectors.map((sector) => (
          <path
            key={sector.type}
            d={sector.d}
            fill={`rgba(${ENTRY_COLORS[sector.type]}, ${
              sector.type === entry ? 0.14 : 0.04
            })`}
            stroke={`rgba(${ENTRY_COLORS[sector.type]}, ${
              sector.type === entry ? 0.3 : 0.16
            })`}
            strokeWidth="1"
          />
        ))}

        {sectors.map((sector) => {
          const isActive = sector.type === entry;
          const label =
            sector.type === "direct"
              ? "DIRECT"
              : sector.type === "parallel"
                ? "PARALLEL"
                : "TEARDROP";

          return (
            <text
              key={`${sector.type}-label`}
              x={fixed(sector.label.x, 1)}
              y={fixed(sector.label.y, 1)}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={isActive ? "10" : "9"}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              fontWeight={200}
              letterSpacing="0.8"
              fill={
                isActive
                  ? `rgb(${ENTRY_COLORS[sector.type]})`
                  : "var(--color-ink-dim)"
              }
              opacity={isActive ? 1 : 0.72}
            >
              {label}
            </text>
          );
        })}

        <circle
          cx={CX}
          cy={CY}
          r={RING}
          fill="none"
          stroke="var(--color-ink-dim)"
          strokeWidth="1"
        />

        {Array.from({ length: 12 }).map((_, index) => {
          const inner = toXY(index * 30, RING - 6);
          const outer = toXY(index * 30, RING);

          return (
            <line
              key={index}
              x1={fixed(inner.x, 1)}
              y1={fixed(inner.y, 1)}
              x2={fixed(outer.x, 1)}
              y2={fixed(outer.y, 1)}
              stroke="var(--color-ink-dim)"
              strokeWidth="1"
            />
          );
        })}

        {["N", "E", "S", "W"].map((label, index) => {
          const point = toXY(index * 90, RING + 15);

          return (
            <text
              key={label}
              x={fixed(point.x, 1)}
              y={fixed(point.y, 1)}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="16"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              fill="var(--color-ink-dim)"
            >
              {label}
            </text>
          );
        })}

        <path
          d={hold.loop}
          fill="none"
          stroke="var(--color-ink-dim)"
          strokeWidth="2"
          markerMid={`url(#${markerId})`}
        />

        <path
          d={entryPath.d}
          fill="none"
          stroke={`rgb(${ENTRY_COLORS[entry]})`}
          strokeWidth="2.5"
          strokeDasharray="6 5"
          strokeLinecap="round"
          markerMid={`url(#${markerId})`}
          markerEnd={`url(#${markerId})`}
        />

      {!reducedMotion && (
        <circle
          r="4.5"
          fill={`rgb(${ENTRY_COLORS[entry]})`}
          stroke="var(--color-panel-deep)"
          strokeWidth="1"
        >
          <animateMotion
            ref={animateRef}
            dur={`${CYCLE_SECONDS}s`}
            begin="0s"
            repeatCount="indefinite"
            calcMode="linear"
            keyTimes={DOT_KEY_TIMES}
            keyPoints={DOT_KEY_POINTS}
            path={motionPath}
          />
        </circle>
      )}
        <circle cx={CX} cy={CY} r="4" fill="var(--color-ink)" />

        <text
          x={CX + 8}
          y={CY + 15}
          fontSize="11"
          fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          fill="var(--color-ink-muted)"
        >
          Fix
        </text>

        <g
          transform={`translate(${fixed(aircraft.x)},${fixed(
            aircraft.y,
          )}) rotate(${heading})`}
          style={{ 
            transition: "transform 0ms ease-out",
            willChange: "transform" 
          }}
        >
          <path
            d="M 0,-12 
               C 1,-12 2.5,-10 2.5,-7 
               L 2.5,-3 
               L 12,2 
               C 13,2.5 12,4 10,3.5 
               L 2.5,1 
               L 2.5,6 
               L 6,9 
               C 6.5,9.5 6,10 5,10 
               L 0,9 
               L -5,10 
               C -6,10 -6.5,9.5 -6,9 
               L -2.5,6 
               L -2.5,1 
               L -10,3.5 
               C -12,4 -13,2.5 -12,2 
               L -2.5,-3 
               L -2.5,-7 
               C -2.5,-10 -1,-12 0,-12 Z"
            fill="var(--color-scope)"
            stroke="var(--color-panel-deep)"
            strokeWidth="1.2"
            strokeLinejoin="round"
            style={{ vectorEffect: "non-scaling-stroke" }}
          />
        </g>
      </svg>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block size-2 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Geometry converted from the working HTML
// ─────────────────────────────────────────────────────────────────────────────

function rad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function toXY(bearing: number, radius: number): Point {
  return {
    x: CX + radius * Math.sin(rad(bearing)),
    y: CY - radius * Math.cos(rad(bearing)),
  };
}

function addByBearing(point: Point, bearing: number, distance: number): Point {
  return {
    x: point.x + distance * Math.sin(rad(bearing)),
    y: point.y - distance * Math.cos(rad(bearing)),
  };
}

function shortTurn(startHeading: number, endHeading: number): Turn {
  const delta = (((endHeading - startHeading) % 360) + 360) % 360;
  return delta <= 180 ? "right" : "left";
}

function arcTo(
  start: Point,
  startHeading: number,
  turn: Turn,
  endHeading: number,
  radius: number,
): ArcResult {
  let center: Point;
  let radialBearingEnd: number;
  let delta: number;
  let sweep: 0 | 1;

  if (turn === "right") {
    center = addByBearing(start, startHeading + 90, radius);
    radialBearingEnd = endHeading - 90;
    delta = (((endHeading - startHeading) % 360) + 360) % 360;
    sweep = 1;
  } else {
    center = addByBearing(start, startHeading - 90, radius);
    radialBearingEnd = endHeading + 90;
    delta = (((startHeading - endHeading) % 360) + 360) % 360;
    sweep = 0;
  }

  const end = addByBearing(center, radialBearingEnd, radius);

  return {
    d: `A ${radius} ${radius} 0 ${delta > 180 ? 1 : 0} ${sweep} ${fixed(
      end.x,
    )} ${fixed(end.y)}`,
    end,
  };
}

function lineTo(point: Point): string {
  return `L ${fixed(point.x)} ${fixed(point.y)}`;
}

function holdGeometry(inbound: number, turn: Turn): HoldGeometry {
  const courseFromFix = inbound + 180;
  const holdingSide = inbound + (turn === "right" ? 90 : -90);
  const outbound = inbound + 180;

  const fix: Point = { x: CX, y: CY };
  const bottom = addByBearing(fix, courseFromFix, LEG_LENGTH);
  const corner = addByBearing(bottom, holdingSide, HOLD_WIDTH);

  const topArc = arcTo(fix, inbound, turn, outbound, TURN_RADIUS);
  const bottomArc = arcTo(corner, outbound, turn, inbound, TURN_RADIUS);

  const loop = [
    `M ${CX} ${CY}`,
    topArc.d,
    lineTo(corner),
    bottomArc.d,
    lineTo(fix),
  ].join(" ");

  const continuation = [
    topArc.d,
    lineTo(corner),
    bottomArc.d,
    lineTo(fix),
  ].join(" ");

  return {
    loop,
    continuation,
  };
}

function buildEntryPath(
  inbound: number,
  heading: number,
  turn: Turn,
): EntryPathResult {
  const sign = turn === "right" ? 1 : -1;
  const outbound = inbound + 180;
  const oppositeTurn: Turn = turn === "right" ? "left" : "right";

  const fix: Point = { x: CX, y: CY };
  const start = toXY((heading + 180) % 360, RING - 35);
  const entry = classifyEntry(heading, inbound, turn);

  let d = `M ${fixed(start.x)} ${fixed(start.y)} ${lineTo(fix)}`;

  if (entry === "direct") {
    return { d, entry };
  }

  if (entry === "teardrop") {
    const teardropTrack = inbound + 180 - 30 * sign;

    const firstArc = arcTo(
      fix,
      heading,
      shortTurn(heading, teardropTrack),
      teardropTrack,
      TURN_RADIUS,
    );

    d += ` ${firstArc.d}`;

    const teardropEnd = addByBearing(firstArc.end, teardropTrack, LEG_LENGTH);

    d += ` ${lineTo(teardropEnd)}`;

    const secondArc = arcTo(
      teardropEnd,
      teardropTrack,
      turn,
      inbound,
      TURN_RADIUS,
    );

    d += ` ${secondArc.d} ${lineTo(fix)}`;

    return { d, entry };
  }

  const firstArc = arcTo(
    fix,
    heading,
    shortTurn(heading, outbound),
    outbound,
    TURN_RADIUS,
  );

  d += ` ${firstArc.d}`;

  const outboundEnd = addByBearing(firstArc.end, outbound, LEG_LENGTH);

  d += ` ${lineTo(outboundEnd)}`;

  const secondArc = arcTo(
    outboundEnd,
    outbound,
    oppositeTurn,
    inbound,
    TURN_RADIUS,
  );

  d += ` ${secondArc.d} ${lineTo(fix)}`;

  return { d, entry };
}

interface SectorWedge {
  type: Entry;
  d: string;
  label: Point;
}

function buildSectorWedges(
  inbound: number,
  turn: Turn,
  activeEntry: Entry,
): SectorWedge[] {
  const rightDefs: Array<[Entry, number, number]> = [
    ["direct", 290, 110],
    ["parallel", 180, 290],
    ["teardrop", 110, 180],
  ];

  const leftDefs: Array<[Entry, number, number]> = [
    ["direct", 250, 70],
    ["parallel", 180, 250],
    ["teardrop", 70, 180],
  ];

  const defs = turn === "right" ? rightDefs : leftDefs;

  return defs.map(([type, min, max]) => {
    const startBearing = inbound + min + 180;
    const endBearing = inbound + max + 180;

    const start = toXY(startBearing, RING);
    const end = toXY(endBearing, RING);

    let arc = max - min;
    arc = ((arc % 360) + 360) % 360;

    const largeArc = arc > 180 ? 1 : 0;

    const d = [
      `M ${CX} ${CY}`,
      `L ${fixed(start.x)} ${fixed(start.y)}`,
      `A ${RING} ${RING} 0 ${largeArc} 1 ${fixed(end.x)} ${fixed(end.y)}`,
      "Z",
    ].join(" ");

    const midpoint = inbound + midpointClockwise(min, max) + 180;

    /**
     * Pull direct closer to center because it is wide.
     * Push teardrop and parallel outward because they are narrower.
     */
    const labelRadius = type === "direct" ? RING * 0.48 : RING * 0.66;
    const label = toXY(midpoint, labelRadius);

    void activeEntry;

    return {
      type,
      d,
      label,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

function midpointClockwise(min: number, max: number): number {
  let arc = max - min;

  arc = ((arc % 360) + 360) % 360;

  return min + arc / 2;
}

function clampCourseInput(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.min(360, Math.max(0, normalizeCourse(value)));
}

function displayCourse(value: number): number {
  return value === 0 ? 360 : value;
}

function fixed(value: number, digits = 2): string {
  return value.toFixed(digits);
}

function usePrefersReducedMotion(): boolean {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    const query = window.matchMedia("(prefers-reduced-motion: reduce)");

    setReducedMotion(query.matches);

    const update = () => setReducedMotion(query.matches);

    query.addEventListener("change", update);

    return () => query.removeEventListener("change", update);
  }, []);

  return reducedMotion;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebounced(value);
    }, delayMs);

    return () => window.clearTimeout(timeout);
  }, [value, delayMs]);

  return debounced;
}
